import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { useHistory } from "react-router";
import CustomerOverviewSearchBar from "@icoach/nonMember/customer/CustomerOverviewSearchBar";
import useOptions from "@util/hook/useOptions";
import useTableScrollerTo from "@util/hook/useTableScrollerTo";
import useNonMemberApi from "@apis/useNonMemberApi";
import { OverviewListLayout, OverviewListToolbar, PageSortAndTotalBox, OverviewListActionBar } from "@icoach/components/overviewList/";
import PageLayout from "@icoach/components/layout/PageLayout";
import OverflowMenu from "@icoach/components/overflowMenu/OverflowMenu";
import useOverview from "@util/hook/useOverview";
import { getCustomerRowsLabel, menuNonMemberActions } from "@icoach/nonMember/nonMember.util";
import { SmsActionBar, AddInvalidActionBar, AddTemporaryActionBar, NonMemberDeleteActionBar } from "@icoach/nonMember/components/";
import usePrivacyToggle, { securityMaskFilterTypeMap } from "@util/hook/usePrivacyToggle";
import useCheckboxGroup from "@icoach/components/checkboxGroup/useCheckboxGroup";
import { getDate, parseDate, getSubMonths } from "@util/moment";
import { isEmpty, isEqual } from "@util/utils";
import { CUSTOMER_VALID_TYPE, CUSTOMER_TYPE, TARGET_TYPE } from "@constants/";
import { NON_MEMBER_ROUTER } from "@icoach/router/routerPath";

/**
 * 預設搜尋參數
 * @type {Object}
 */
const defaultSearchParams = {
	pageIndex: 1,
	pageSize: 10,
	name: "", // 輸入框
	isAdvanced: true,
	startDate: parseDate(getSubMonths(new Date(), 6)), // 詢問日期
	endDate: getDate(), // 詢問日期
	required: [], // 需求
	source: [], //
	askRecordType: [], // 詢問方式
	customerStatus: [], // 狀態
	validType: CUSTOMER_VALID_TYPE.VALIDLIST, // 有效無效
};

const CustomerOverviewPage = ({ dialogRef: _dialogRef }) => {
	const customerOverviewSearchBarRef = useRef(null);
	const history = useHistory();
	const tableRef = useRef(null);
	const { setCache, doScrollerTo, domKeyName } = useTableScrollerTo({ tableRef: tableRef, cacheKeyName: "nonmember-scroller", domKeyName: "data-customer-id", uniqueIdName: "customerID" });
	const { getInitCustomerOptionsApi, postOverviewCustomerApi } = useNonMemberApi();
	const { isRevealed, toggle, renderPrivacyIcon } = usePrivacyToggle();
	const { options } = useOptions({
		getOptionsApi: () => getInitCustomerOptionsApi(),
		isFetchOnMount: true,
	});
	const { updateOverviewPage, searchParams, overviewData, getOverviewData } = useOverview({
		getOverviewApi: postOverviewCustomerApi,
		defaultSearchParams: defaultSearchParams,
		enableInfiniteScroll: true,
	});

	const { getResult, Checkbox, CheckboxAll: CheckboxAllActionBar, onResetCheckBox, onUpdateCheckBox } = useCheckboxGroup({ tableRef });

	const { list, pagination } = overviewData || {};

	const listRef = useRef([]);
	const nonMemberDescribeLabelBoxRef = useRef(null);

	// 簡訊是共用的所以來源要用 TargetType
	const handleCheckedData = () => {
		const target = getResult();
		if (target.length === 1) {
			const t = list.filter(({ customerID }) => isEqual(customerID, target[0]));
			return t.map(item => ({ id: item.customerID, name: item.name, type: TARGET_TYPE.CUSTOMER }));
		} else {
			return target.map(id => ({ id, type: TARGET_TYPE.CUSTOMER }));
		}
	};

	// 組暫存名單使用的搜尋條件
	const handleAddTemporaryData = () => {
		const { validType, source, required, customerStatus, askRecordType } = searchParams;
		const { validTypes: validTypeOptions, customerSourceTypes, requiredTypes, customerStatus: customerStatusOptions, askRecordTypes: askRecordTypesOptions } = options;
		const sourceSplit = Array.isArray(source) ? source : [source];
		const requiredSplit = Array.isArray(required) ? required : [required];
		const customerStatusSplit = Array.isArray(customerStatus) ? customerStatus : [customerStatus];
		const askRecordTypeSplit = Array.isArray(askRecordType) ? askRecordType : [askRecordType];
		const ids = getResult();
		let text = [];

		text.push(...validTypeOptions.filter(({ value }) => isEqual(value, validType)).map(({ text }) => text));
		text.push(...customerSourceTypes.filter(({ value }) => sourceSplit.some(v => isEqual(v, value))).map(({ text }) => text));
		text.push(...requiredTypes.filter(({ value }) => requiredSplit.some(v => isEqual(v, value))).map(({ text }) => text));
		text.push(...customerStatusOptions.filter(({ value }) => customerStatusSplit.some(v => isEqual(v, value))).map(({ text }) => text));
		text.push(...askRecordTypesOptions.filter(({ value }) => askRecordTypeSplit.some(v => isEqual(v, value))).map(({ text }) => text));

		const params = {
			note: text.join("、"),
			list: ids,
			type: CUSTOMER_TYPE.CUSTOMER,
		};
		return params;
	};

	// 新增無效名單資料 多選 / menu單選
	const handleAddInvalidData = target => {
		const ids = isEmpty(target) ? getResult() : target;
		return {
			list: ids,
			typeText: "顧客",
		};
	};

	// 刪除顧客 多選 / menu單選
	const handleDeleteData = target => {
		const ids = isEmpty(target) ? getResult() : target;
		const info = listRef.current.filter(({ customerID }) => ids.some(id => isEqual(id, customerID))).map(({ name, cellPhone }) => ({ name, cellPhone }));
		return {
			list: ids,
			typeText: "顧客",
			info: info,
		};
	};

	const handleAddInvalidRefresh = () => {
		handleSubmit();
	};

	const handleDeleteRefresh = () => {
		handleSubmit();
	};

	const handleContactRefresh = () => {
		handleSubmit();
	};

	const handleSubmit = () => {
		const params = customerOverviewSearchBarRef.current.getResult();
		params.pageIndex = 1;
		params.pageSize = 10;
		doOverviewApi(params);
	};

	const handlePageChange = (e, page) => {
		doPageChangeApi(page);
	};

	// 預約下拉選單事件
	const handleAppointmentMenuClick = useCallback(
		cID => {
			const targetData = listRef.current;
			setCache(targetData, cID, searchParams);
			history.push(NON_MEMBER_ROUTER.CUSTOMER.GET_ADD_PATH(cID));
		},
		// eslint-disable-next-line
		[searchParams],
	);

	const handleToggleRevealed = useCallback(
		() => {
			const apiParams = {
				filterType: securityMaskFilterTypeMap.ClubCustomerOverview,
				totalCount: listRef.current.length,
			};
			toggle(apiParams);
		},
		// eslint-disable-next-line
		[toggle],
	);

	const doOverviewApi = async par => {
		await getOverviewData(par);
		onResetCheckBox();
	};

	const doPageChangeApi = async page => {
		await updateOverviewPage(page);
		onUpdateCheckBox();
	};

	const headerRow = useMemo(
		() => {
			const mAction = menuNonMemberActions({
				dialogRef: _dialogRef,
				onAddInvalidData: handleAddInvalidData,
				onDeleteData: handleDeleteData,
				onAddInvalidRefresh: handleAddInvalidRefresh,
				onDeleteRefresh: handleDeleteRefresh,
				onAppointmentClick: handleAppointmentMenuClick,
				onContactRefresh: handleContactRefresh,
			});
			const dropsMenu = [
				...getCustomerRowsLabel(),
				{
					cellKey: "customerID",
					headerLabel: "",
					align: "center",
					formatCell: (cellValue, rowsData) => {
						return <OverflowMenu menuAction={mAction} sourceData={rowsData} nonMemberDescribeLabelBoxRef={nonMemberDescribeLabelBoxRef} />;
					},
				},
			];

			return dropsMenu;
		},
		// eslint-disable-next-line
		[],
	);

	// 盡量不要用，會不好理解和破壞render整體結構 by Allen
	useEffect(
		() => {
			listRef.current = list;
			if (list.length > 0) {
				doScrollerTo();
			}
		},
		// eslint-disable-next-line
		[list],
	);

	const { validType } = searchParams;
	return (
		<OverviewListLayout>
			<PageLayout.Header title="顧客總覽" />
			<OverviewListLayout.SearchBar className={"mb-2"} onSubmit={handleSubmit}>
				<CustomerOverviewSearchBar key={options.length} ref={customerOverviewSearchBarRef} options={options} sourceData={searchParams} />
			</OverviewListLayout.SearchBar>
			<OverviewListToolbar>
				{list.length !== 0 && (
					<OverviewListActionBar>
						<CheckboxAllActionBar />
						<SmsActionBar getResult={handleCheckedData} dialogRef={_dialogRef} />
						<AddTemporaryActionBar getResult={handleAddTemporaryData} dialogRef={_dialogRef} />
						{!isEqual(validType, CUSTOMER_VALID_TYPE.INVALIDLIST) && <AddInvalidActionBar getResult={handleAddInvalidData} dialogRef={_dialogRef} refresh={handleAddInvalidRefresh} />}
						<NonMemberDeleteActionBar getResult={handleDeleteData} dialogRef={_dialogRef} refresh={handleDeleteRefresh} />
						{renderPrivacyIcon(handleToggleRevealed, "has-divider")}
					</OverviewListActionBar>
				)}
				<PageSortAndTotalBox pageIndex={pagination.pageIndex} pageSize={pagination.pageSize} totalCount={pagination.totalCount} isCurrentPageLastIndex />
			</OverviewListToolbar>
			<OverviewListLayout.Table
				headerRow={headerRow}
				list={list}
				totalPage={overviewData?.pagination?.totalPage}
				pageIndex={overviewData?.pagination?.pageIndex}
				isMasonryPagination
				onChangePage={handlePageChange}
				tableBodyProps={{ ref: tableRef }}
				tableRowBodyProps={{ Checkbox: Checkbox, isRevealed: isRevealed, domKeyName: domKeyName, nonMemberDescribeLabelBoxRef: nonMemberDescribeLabelBoxRef }}
			/>
		</OverviewListLayout>
	);
};

export default CustomerOverviewPage;
